// Pipeline stage labels + colors. Mirrors backend `PIPELINE_STAGES`.
// NB: internal value `qualified` is displayed as "Discovery" — kept the
// internal key for backward-compat with existing DB records.
// Spam IS a valid pipeline stage (must be selectable on the detail page so the
// SpamReasonDialog can fire) but is intentionally absent from KANBAN_STAGES
// because spam doesn't get its own drag-and-drop column.
export const PIPELINE_STAGES = [
  "new", "contacted", "qualified", "quote_sent", "negotiation", "won", "lost", "spam",
];

// Stages that appear as columns on the Kanban pipeline view.
// `spam` and `lost` are intentionally treated as terminal — `lost` shows as the
// last column but `spam` is hidden from the board (filterable in the table).
export const KANBAN_STAGES = [
  "new", "contacted", "qualified", "quote_sent", "negotiation", "won", "lost",
];

export const STAGE_LABEL = {
  new: "New",
  contacted: "Contacted",
  qualified: "Discovery",
  quote_sent: "Quote Sent",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
  spam: "Spam",
};

export const STAGE_BADGE = {
  new:         "bg-blue-100 text-blue-800 border-blue-200",
  contacted:   "bg-violet-100 text-violet-800 border-violet-200",
  qualified:   "bg-amber-100 text-amber-800 border-amber-200",
  quote_sent:  "bg-cyan-100 text-cyan-800 border-cyan-200",
  negotiation: "bg-orange-100 text-orange-800 border-orange-200",
  won:         "bg-emerald-100 text-emerald-800 border-emerald-200",
  lost:        "bg-rose-100 text-rose-800 border-rose-200",
  spam:        "bg-slate-200 text-slate-700 border-slate-300",
};

export const FORM_TYPE_LABEL = {
  contact: "Contact",
  quote: "Quote",
  fast_quote: "Fast Quote",
  sample_request: "Sample Request",
  roi_playbook: "ROI Playbook",
  quick_ship_sample: "Quick Ship Sample",
  custom_project: "Custom Project",
  schedule_call: "Schedule Call",
  request_tester: "Tester Request",
  consultation_intake: "Consultation Intake",
  consultation_booking: "Consultation Booking",
};

export const formatFormType = (t) => FORM_TYPE_LABEL[t] || t || "—";

// Dashboard user roles
export const ROLE_LABEL = {
  admin: "Administrator",
  operations: "Operations",
  sales_rep: "Sales",
  read_only: "Read-only",
};
export const ROLES_INVITE = ["sales_rep", "operations", "read_only", "admin"];
export const isReadOnly = (user) => user?.role === "read_only";

// Spam classification reasons (matches backend SPAM_REASONS)
export const SPAM_REASONS = [
  { value: "bot_ai",                label: "Bot / AI Generated" },
  { value: "vendor_solicitation",   label: "Vendor Solicitation" },
  { value: "marketing_db",          label: "Marketing Database Sales" },
  { value: "duplicate",             label: "Duplicate Submission" },
  { value: "other",                 label: "Other (specify)" },
];
export const SPAM_REASON_LABEL = Object.fromEntries(SPAM_REASONS.map((r) => [r.value, r.label]));

// Phase B — Lead Type classification (independent of pipeline stage)
export const LEAD_TYPES = [
  { value: "new_prospect",      label: "New Prospect" },
  { value: "existing_customer", label: "Existing Customer" },
  { value: "repeat_inquiry",    label: "Repeat Inquiry" },
  { value: "service_request",   label: "Service Request" },
];
export const LEAD_TYPE_LABEL = Object.fromEntries(LEAD_TYPES.map((t) => [t.value, t.label]));
export const LEAD_TYPE_BADGE = {
  new_prospect:      "bg-sky-100 text-sky-800 border-sky-200",
  existing_customer: "bg-emerald-100 text-emerald-800 border-emerald-200",
  repeat_inquiry:    "bg-amber-100 text-amber-800 border-amber-200",
  service_request:   "bg-violet-100 text-violet-800 border-violet-200",
};

export const formatMoney = (n) => {
  if (n === null || n === undefined) return "$0";
  const num = Number(n);
  if (!Number.isFinite(num)) return "$0";
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${Math.round(num / 1_000)}k`;
  return `$${num}`;
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const getDisplayName = (s) => {
  if (!s) return "—";
  if (s.name) return s.name;
  const parts = [s.firstName, s.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : (s.email || "Unknown");
};
